/**
 * The browser tab title: "<page> · <site name>" instead of just the site name, so the
 * history / back menu and open tabs say which page each one is.
 *
 * The page part comes from, in order:
 *  1. a fixed name for the main sections (Dashboard, My Learning, …);
 *  2. the page's own heading — the first <h1> in the main area (a course's or program's
 *     name, "Course Report", "Assignments", …), followed as the page loads / changes;
 *  3. nothing (the site name alone).
 * The site name is the tenant's (BrandingContext calls setSiteNameForTitle).
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { tr } from '@/i18n';

let siteName = 'iLearn';

const apply = (page: string | null) => {
  const clean = (page || '').replace(/\s+/g, ' ').trim().slice(0, 90);
  const title = clean && clean !== siteName ? `${clean} · ${siteName}` : siteName;
  if (document.title !== title) document.title = title;
};

let currentPage: string | null = null;

/** The tenant's name for the title (called by the branding loader). */
export const setSiteNameForTitle = (name: string | null | undefined) => {
  siteName = (name || '').trim() || 'iLearn';
  apply(currentPage);
};

// Fixed names for the main sections (exact paths); everything else uses its heading.
const SECTION_TITLES: [RegExp, () => string][] = [
  [/^\/user\/dashboard\/?$/, () => tr('nav.dashboard', 'Dashboard')],
  [/^\/user\/my-courses\/?$/, () => tr('ui.my_learning', 'My Learning')],
  [/^\/user\/courses\/?$/, () => tr('ui.explore', 'Explore')],
  [/^\/user\/saved\/?$/, () => tr('ui.saved', 'Saved')],
  [/^\/user\/reports\/?$/, () => tr('myreports.title', 'My reports')],
  [/^\/user\/team-hub\/compliance/, () => tr('teamhub.team_compliance', 'Team compliance')],
  [/^\/user\/team-hub/, () => tr('widgets.my_team_name', 'My Team')],
  [/^\/user\/skills-rating/, () => tr('skills.srt_title', 'Skills Rating')],
  [/^\/user\/talent-grid/, () => tr('ui.talent_grid', 'Talent Grid')],
  [/^\/user\/teaching/, () => tr('ui.teaching', 'Teaching')],
  [/^\/user\/messages/, () => tr('ui.messages', 'Messages')],
  [/^\/user\/notifications/, () => tr('ui.notifications', 'Notifications')],
  [/^\/user\/profile\/?$/, () => tr('ui.profile', 'Profile')],
  [/^\/admin\/insight-builder\/?(system|user)?\/?$/, () => tr('insight.page_title', 'Insight Builder')],
];

const headingText = (): string | null => {
  const h = document.querySelector('main h1') || document.querySelector('h1');
  const text = h?.textContent?.trim();
  return text || null;
};

export const PageTitleSync: React.FC = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const fixed = SECTION_TITLES.find(([re]) => re.test(pathname));
    if (fixed) {
      currentPage = fixed[1]();
      apply(currentPage);
      return undefined;
    }
    // Follow the page's heading while it loads (and when it changes, e.g. a renamed course).
    let timer: number | undefined;
    const update = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        currentPage = headingText();
        apply(currentPage);
      }, 150);
    };
    update();
    const observer = new MutationObserver(update);
    observer.observe(document.body, { childList: true, subtree: true, characterData: true });
    return () => {
      observer.disconnect();
      window.clearTimeout(timer);
    };
  }, [pathname]);

  return null;
};

export default PageTitleSync;
